import { useQuery } from "@tanstack/react-query";
import analyticsRepository from "@/repository/analyticsRepository";
import { DailyUsageCount, FeedbackCount, IntentRanking, WeekHourlyCount } from "@/model/Analytics";


const getData = async (id?: string, startDate?: string, endDate?: string) => {
    if (!id || !startDate || !endDate) {
        return null;
    }

    const apis = [
        [ "messagingUserCount", analyticsRepository.getMessagingUserCount ],
        [ "userMessageCount", analyticsRepository.getUserMessageCount ],
        [ "fallbackMessageCount", analyticsRepository.getFallbackMessageCount ],
        [ "effectiveConversationCount", analyticsRepository.getEffectiveConversationCount ],
        [ "dailyUsageCount", analyticsRepository.getDailyUsageCount ],
        [ "weekHourlyMessageCount", analyticsRepository.getWeekHourlyMessageCount ],
        [ "intentRanking", analyticsRepository.getIntentRanking ],
        [ "feedback", analyticsRepository.getFeedback ]
    ] as const;

    const responses = await Promise.all(apis.map(([key, promise]) => 
        promise(id, startDate, endDate).then(response => [key, response.result])
    ));
    return Object.fromEntries(responses);
};

interface DashboardAnalyticsResponse {
    messagingUserCount: number;
    userMessageCount: number;
    fallbackMessageCount: number;
    effectiveConversationCount: number;
    dailyUsageCount: DailyUsageCount[];
    weekHourlyMessageCount: WeekHourlyCount[];
    intentRanking: IntentRanking;
    feedback: FeedbackCount[];
}

export const useDashboardAnalytics = (id: string, startDate?: string, endDate?: string) => {
    return useQuery<DashboardAnalyticsResponse>({
        queryKey: ['analytics', 'dashboard', id, startDate, endDate],
        queryFn: () => getData(id, startDate, endDate),
        staleTime: 1000 * 60 * 3,
    });
};
